import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

/** Shares are percentages, so a full bucket reads 100 rather than 1. */
const SHARE_FULL = 100;

export type MediaSentimentOverTimeView = {
  /** `Worst day` — a day, finer than the weekly plot on purpose. */
  kpis: KPIMetric[];
  /** One row per week; each sentiment key holds its share (null for a week with no stories). */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any story is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** The story count behind a share, for the tooltip. */
  formatDetail: (row: ChartRow, seriesKey: string) => string | undefined;
};

export type MediaSentimentOverTimeCardProps = WidgetBlockProps & {
  view?: MediaSentimentOverTimeView;
  /** Opens the stories behind a week (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:18143` — the tone of your press coverage, week by week.
 *
 * **Weekly, explicitly** — the one place this module overrides `autoBucket`,
 * and it is a measurement rather than a preference. Press volume is a tenth of
 * social volume: daily buckets put roughly one story on each, and a share
 * computed over one story can only read 0% or 100%. Weekly gives eleven buckets
 * of 3–11 stories, which is a trend. A week nobody published in is a gap, not a
 * claim that every tone lost the conversation at once.
 *
 * The worst day is ranked by the count of negative stories, not by negative
 * share (at ~1 story a day every single critical piece would tie at 100%); ties
 * break towards the day with the least positive coverage alongside it.
 */
export function MediaSentimentOverTimeCard({
  status,
  view,
  span = 12,
  chrome,
  onRetry,
  onViewDetails,
}: MediaSentimentOverTimeCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No coverage in this period"
      {...cardState(status, hasData, onRetry)}
      title="Media sentiment over time"
      helpText="How the tone of your press coverage has moved through the period, as each week's share of the stories published."
      helpSupportingText="Weekly rather than daily: press volume is a tenth of social volume, and a share computed over a single story can only read 0% or 100%. The figure above is a single day, which is finer than the plot on purpose — the line is the trend, the KPI is the worst moment in it."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            /*
             * Pinned to the full scale. A share's range is a fact about the metric
             * rather than something the data happened to do.
             */
            yDomain={[0, SHARE_FULL]}
            /* Pinned domain, pinned ticks — or Recharts picks badly. See rules §4. */
            yTicks={[0, 20, 40, 60, 80, SHARE_FULL]}
            formatValue={view.formatValue}
            formatAxisValue={(v) => `${Math.round(v)}%`}
            /* The story count behind the share. At ~1 story a day this is the
               difference between a 100% negative week and a bad month. */
            formatDetail={view.formatDetail}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
